/**
 * PointText — fragments as world-anchored text.
 *
 * Replaces the DOM fragment cards. Those were flat rectangles billboarded to the
 * lens: legible, but they read as an overlay on a photograph rather than as
 * something in the room. This is real letterforms — a textured quad per glyph,
 * sampling the shared atlas built in glyphs.ts — on a plane that is committed to
 * the world, foreshortening as you walk past it and assembling letter by letter.
 *
 * ⚠️ The name is a holdover from an earlier version that rendered each glyph as
 * a scattered handful of points rather than a solid shape — legible in
 * principle, but a device capture (2026-08-25) showed it reading as "crumbs"
 * rather than words at ordinary viewing distances. Kept unrenamed so this diff
 * stays about the rendering, not a file move; a future pass can call it
 * `WorldText` once nothing else depends on the old name.
 *
 * ── What is frozen, and why ───────────────────────────────────────────────
 * Two things are captured the first time a fragment is seen and never
 * recomputed:
 *
 *  - **Facing.** The plane's normal is the direction back toward wherever the
 *    camera was standing when the fragment appeared. Recomputing it per frame
 *    is billboarding by accident, which is the exact failure this exists to
 *    avoid.
 *  - **Em size.** Derived from how far away the anchor was at spawn, then held
 *    in world units. Scaling it per frame would keep apparent size constant,
 *    which is again billboarding, just on the other axis.
 *
 * ── Assembly ──────────────────────────────────────────────────────────────
 * Letters fall along the plane's own up vector in reading order, so text lying
 * flat on a table slides in across the surface rather than dropping through it.
 * The stagger is a shader function of a per-glyph attribute — no CPU cost per
 * frame, which matters because this runs alongside a SLAM loop. A whole letter
 * moves as one rigid shape (see TextRenderer's a_seed), not a dissolving smear.
 */

import { useEffect, useRef, type RefObject } from 'react';
import { useStore } from '../../state/store.js';
import { runtime } from '../../session/runtime.js';
import { viewGeometry } from '../../spatial/cameraFov.js';
import type { Vec3 } from '../../spatial/SpatialBackend.js';
import { TextRenderer, type TextDrawItem } from './TextRenderer.js';
import { layoutText, type TextCloud } from './textCloud.js';
import { placeText, textPlaneBasis } from './textPlane.js';
import { PIGMENT, hexToRgb } from '../Iconostasis/pigments.js';
import { observeCanvasSize } from '../canvasSize.js';

/** Seconds a fragment takes to write itself. */
const ASSEMBLE_SEC = 1.9;
/**
 * Em height as a fraction of the anchor's distance at spawn.
 *
 * Was 0.036, which at a 2.5 m anchor gave a ~9 cm cap height — legible in
 * principle, but too few pixels once the 0.6 PS1 backing scale was applied, so
 * the earlier point-cloud glyphs read as faint noise rather than writing.
 * Doubling it, together with the anchors moving further out, keeps the angular
 * size similar while giving each glyph enough pixels to actually resolve. Now
 * that glyphs are solid antialiased quads rather than a point scatter, this is
 * a smaller concern than it was — but the value is unchanged since nothing
 * here argued for a different one.
 */
const EM_OF_DISTANCE = 0.075;
/** Wrap width, in ems. */
const MAX_WIDTH_EM = 13;

interface Entry {
  cloud: TextCloud;
  /** Plane normal, frozen at first sight. */
  facing: Vec3;
  /** Em size in world units, frozen at first sight. */
  em: number;
  /**
   * How far away the anchor was at first sight. Only a FALLBACK reference for
   * the distance fades — the live `anchor.placementDistance` is preferred,
   * because a provisional anchor's depth is still being corrected and the fade
   * window has to follow it. Backends that do not model placement depth have
   * no such value, hence keeping this.
   */
  spawnDist: number;
  bornAt: number;
  text: string;
}

const LEAD = hexToRgb(PIGMENT.leadWhite);
/**
 * The Watcher's own lines are drawn here like any other fragment, anchored on
 * the object the model named (runtime.ts `anchorObservation`). `Directive.tsx`
 * speaks the same line in screen space as it arrives; that pairing — spoken
 * now, written where you were — is deliberate and is the piece's device.
 *
 * ⚠️ It was briefly not so: between 2026-08-26 and 2026-08-28 this loop
 * skipped `originSession === 'watcher'` to remove a duplicate. The duplicate
 * was real but was between two SCREEN-space renderings (Directive and
 * WatcherEye's `.observation`), and the skip removed the world-space copy
 * instead — deleting the grounding rather than the repetition. Do not
 * reintroduce it; if the Watcher's line ever looks doubled again, look for a
 * second screen-space renderer first.
 */

export interface PointTextProps {
  /**
   * The passthrough <video>. Needed so projection uses the SAME view geometry
   * as Field.tsx: with no video, `viewGeometry` falls back to a 3:4 frame with no
   * object-fit: cover crop, which drew world text ~60% too wide on a tall phone.
   * Absent under WebXR (no <video>), where the fallback is all there is.
   */
  videoRef?: RefObject<HTMLVideoElement>;
}

export function PointText({ videoRef }: PointTextProps = {}) {
  const videoRefLive = useRef(videoRef);
  videoRefLive.current = videoRef;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const live = useStore((s) => s.live);
  const liveRef = useRef(live);
  liveRef.current = live;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const r = new TextRenderer(canvas);
    if (!r.ready) return;

    const entries = new Map<string, Entry>();

    const unobserve = observeCanvasSize(canvas, (w, h, dpr) => r.resize(w, h, dpr));

    let raf = 0;
    const t0 = performance.now();

    const loop = (now: number) => {
      const timeSec = (now - t0) / 1000;
      const fragments = liveRef.current;
      const cam = runtime.spatial.cameraPose?.();
      // Per frame, like Field.tsx: the video's intrinsic size and the element's
      // display size can both change (stream negotiation, rotation, resize).
      const view = viewGeometry(videoRefLive.current?.current ?? null);
      const W = window.innerWidth;
      const H = window.innerHeight;

      if (!cam) {
        r.clear();
        raf = requestAnimationFrame(loop);
        return;
      }

      const items: TextDrawItem[] = [];
      const seen = new Set<string>();

      for (const f of fragments) {
        seen.add(f.id);
        // The Watcher's lines ARE drawn here, on the object they named — see
        // runtime.ts's anchorObservation(). This used to `continue` past them.
        //
        // That skip was added on 2026-08-26 to kill a duplicate, and it
        // removed the wrong copy. The real duplicate was two SCREEN-space
        // renderings — Directive.tsx and WatcherEye's own `.observation`
        // block — and the skip deleted the world-space one instead, which was
        // the whole point of the Watcher naming an object and returning a
        // bounding box for it. WatcherEye's copy is gone now; this one comes
        // back.
        const anchor = runtime.spatial.resolve(f.id) ?? runtime.spatial.anchor(f.id);
        // Tolerate an unresolved or untrusted anchor — the backend deliberately
        // withholds confidence until the map is worth pinning a coordinate to.
        if (!anchor || anchor.confidence <= 0) continue;

        let e = entries.get(f.id);
        if (!e || e.text !== f.text) {
          const world = anchor.world;
          const toCam: Vec3 = {
            x: cam.position.x - world.x,
            y: cam.position.y - world.y,
            z: cam.position.z - world.z,
          };
          const dist = Math.hypot(toCam.x, toCam.y, toCam.z) || 1;
          e = {
            cloud: layoutText(f.text, { maxWidth: MAX_WIDTH_EM }),
            facing: { x: toCam.x / dist, y: toCam.y / dist, z: toCam.z / dist },
            em: Math.max(0.02, dist * EM_OF_DISTANCE),
            spawnDist: dist,
            bornAt: timeSec,
            text: f.text,
          };
          entries.set(f.id, e);
          r.release(f.id);
        }
        if (e.cloud.count === 0) continue;

        const { right, up } = textPlaneBasis(e.facing, cam);
        // Layout puts the origin at the left edge of the LAST baseline, so shift
        // down by the block height to keep the anchor at the block's centre.
        const origin: Vec3 = {
          x: anchor.world.x - right.x * (e.cloud.width * e.em) / 2 - up.x * (e.cloud.height * e.em) / 2,
          y: anchor.world.y - right.y * (e.cloud.width * e.em) / 2 - up.y * (e.cloud.height * e.em) / 2,
          z: anchor.world.z - right.z * (e.cloud.width * e.em) / 2 - up.z * (e.cloud.height * e.em) / 2,
        };

        // The anchor's own placement distance, tracking depth refinement as a
        // provisional anchor settles. Absolute fade distances were what made
        // well-placed text invisible from birth — see textPlane.ts.
        const ref = anchor.placementDistance ?? e.spawnDist;
        const placed = placeText(origin, right, up, e.em, cam, view, W, H, ref);
        if (!placed.visible) continue;

        const assembly = Math.min(1, (timeSec - e.bornAt) / ASSEMBLE_SEC);
        // Every fragment, the Watcher's included, is written in lead white —
        // the colour an inscription is written in. (Watcher lines are drawn
        // here: see the note above the component. A comment here used to claim
        // they never reach this point, which has been false since 2026-08-28.)
        const colour = LEAD;

        items.push({
          id: f.id,
          cloud: e.cloud,
          ox: placed.ox,
          oy: placed.oy,
          bx: placed.bx,
          by: placed.by,
          assembly,
          // Vitality floors much higher than before. `placed.alpha` already
          // multiplies grazing, distance and edge fades together, so folding a
          // low-vitality factor in on top compounded four dimmings at once and
          // was most of why text washed out.
          alpha: placed.alpha * Math.min(1, 0.62 + f.vitality * 0.38),
          colour,
        });
      }

      // Drop buffers for fragments that have died, or the GPU accumulates every
      // sentence the session ever produced.
      for (const id of [...entries.keys()]) {
        if (!seen.has(id)) {
          entries.delete(id);
          r.release(id);
        }
      }

      r.draw(items);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      unobserve();
      r.dispose();
    };
  }, []);

  return <canvas ref={canvasRef} className="text-canvas" />;
}
